import { useQueryClient } from '@tanstack/react-query';
import * as React from 'react';

import { NOTIFICATIONS_QUERY_ROOT } from '@/api/use-notifications';
import { useNotificationBanner } from '@/components/notification-banner';
import {
  connectNotificationSocket,
  type NotificationSocketHandlers,
} from '@/realtime/notification-socket';

// =============================================================================
// use-notification-realtime.ts — Abonnement TEMPS RÉEL du centre de notifications (R15-COM-06, DEC-31).
// =============================================================================
// À réception du signal socket `notification:new` (payload MINCE) :
//   1. INVALIDE les queries `['notifications', ...]` (summary + list) ⇒ refetch REST (RLS, autorité serveur) ;
//   2. déclenche le BANDEAU in-app silencieux (générique, aucun contenu de signal).
// Le socket est chargé via un IMPORT DYNAMIQUE isolé (notification-socket) branché par un SEAM `connect`
// (défaut = réel ; les tests injectent un faux). REPLI POLLING : `pollIntervalMs` (défaut 30 s) rafraîchit
// périodiquement summary+list — filet de sécurité si le socket est indisponible (aucune régression de
// fraîcheur, tsc/tests restent verts sans dépendre du transport).

/** Signature du connecteur temps réel (seam injectable pour les tests). */
export type NotificationConnect = (
  handlers: NotificationSocketHandlers,
) => () => void;

export interface UseNotificationRealtimeOptions {
  /** Active l'abonnement (défaut true). Passé à false hors session fournisseur. */
  readonly enabled?: boolean;
  /** Connecteur temps réel injectable (défaut = socket réel isolé). */
  readonly connect?: NotificationConnect;
  /** Intervalle du repli polling REST en millisecondes (défaut 30 000). `0` désactive le repli. */
  readonly pollIntervalMs?: number;
}

/**
 * Monte l'abonnement temps réel + le repli polling. Ne rend rien (hook de coordination). Idempotent au
 * démontage : la déconnexion est appelée en nettoyage d'effet (annule un chargement de socket en cours).
 */
export function useNotificationRealtime(
  options: UseNotificationRealtimeOptions = {},
): void {
  const {
    enabled = true,
    connect = connectNotificationSocket,
    pollIntervalMs = 30_000,
  } = options;
  const queryClient = useQueryClient();
  const banner = useNotificationBanner();

  // Réaction stable au signal : invalidation (refetch REST) + bandeau silencieux. Aucune lecture de contenu.
  const onSignal = React.useCallback((): void => {
    void queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_ROOT });
    banner.signal();
  }, [queryClient, banner]);

  // Abonnement socket (seam). Nettoyage → déconnexion (annule le chargement dynamique en cours).
  React.useEffect(() => {
    if (!enabled) {
      return;
    }
    const disconnect = connect({ onSignal });
    return disconnect;
  }, [enabled, connect, onSignal]);

  // Repli POLLING REST : rafraîchit summary+list à intervalle fixe (filet de sécurité si socket indisponible).
  React.useEffect(() => {
    if (!enabled || pollIntervalMs <= 0) {
      return;
    }
    const timer = setInterval((): void => {
      void queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_ROOT });
    }, pollIntervalMs);
    return () => {
      clearInterval(timer);
    };
  }, [enabled, pollIntervalMs, queryClient]);
}
